import { Link } from '../components/SmartLink';
import { useMemo, useState } from 'react';
import { Navigate, useLoaderData, useParams } from 'react-router-dom';

import { motion } from 'motion/react';
import {
  AirplaneTilt,
  ArrowRight,
  Buildings,
  CalendarBlank,
  CaretDown,
  CheckCircle,
  Clock,
  Heart,
  Info,
  MapPin,
  PaperPlaneTilt,
  ShieldCheck,
  Star,
  SuitcaseRolling,
  Users,
  Warning,
  WhatsappLogo,
  XCircle,
} from '@phosphor-icons/react';
import SEO from '../components/SEO';
import { PACKAGE_DETAILS, PACKAGES, type Package, type PackageDetail } from '../data/tourPackagesData';
import type { TourDetail as SanityTourDetail } from '../data/tourData';
import { useTourPackage, type RelatedPackage } from '../hooks/useTourPackage';
import type { TourPackageDetailLoaderData } from '../lib/sanityTourPackages';
import { buildWAUrl } from '../lib/whatsapp';
import { urlFor } from '../lib/urlFor';
import {
  AGENCY_SCHEMA,
  breadcrumbSchema,
  faqSchema,
  packageSchema,
  serviceSchema,
} from '../lib/seoSchemas';

type DetailItineraryDay = {
  day: number;
  title: string;
  description?: string;
  accommodation?: string;
  meals?: string[];
};

type DetailHotel = {
  name: string;
  location?: string;
  rating?: number;
};

type DetailView = {
  slug: string;
  title: string;
  destination: string;
  region?: string;
  themes: string[];
  days: number;
  nights: number;
  priceFromAED: number;
  heroImage: string;
  imageAlt: string;
  gallery: { url: string; alt: string }[];
  description: string[];
  highlights: string[];
  itinerary: DetailItineraryDay[];
  inclusions: string[];
  exclusions: string[];
  hotels: DetailHotel[];
  hotelStars?: number;
  bestTime?: string;
  visaInfo?: string;
  flightDuration?: string;
  groupSize?: string;
  whatToBring: string[];
  cancellationPolicy?: string;
  faqs: { q: string; a: string }[];
  badge?: string;
};

type RelatedView = {
  slug: string;
  title: string;
  destination: string;
  image: string;
  imageAlt: string;
  nights: number;
  days: number;
  priceFromAED: number;
};

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=1600&q=85&fm=webp';

const fallbackFaqs = [
  {
    q: 'Can this package be customized?',
    a: 'Yes, most holiday packages can be customized based on travel dates, hotel category, budget, and traveler preferences.',
  },
  {
    q: 'Does this package include flights?',
    a: 'Package inclusions vary. Please check the included section or contact us on WhatsApp for the latest flight and hotel options.',
  },
  {
    q: 'Is visa assistance included?',
    a: 'Visa guidance may be provided where applicable. Requirements depend on destination, nationality, and current rules.',
  },
  {
    q: 'Are prices fixed?',
    a: 'Prices are starting rates and can change based on travel dates, availability, airline fares, hotel category, and package inclusions.',
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.18 },
  transition: { duration: 0.45 },
};

function formatPrice(price: number) {
  return price > 0 ? `AED ${price.toLocaleString()}` : 'Ask for price';
}

function durationLabel(pkg: Pick<DetailView, 'days' | 'nights'>) {
  return pkg.days > 0 && pkg.nights > 0 ? `${pkg.days} Days / ${pkg.nights} Nights` : 'Flexible Duration';
}

function staticToView(pkg: PackageDetail): DetailView {
  return {
    slug: pkg.id,
    title: pkg.title,
    destination: pkg.location,
    region: pkg.location,
    themes: pkg.categories,
    days: pkg.days,
    nights: pkg.nights,
    priceFromAED: pkg.priceFromAED,
    heroImage: pkg.heroGallery[0]?.url ?? pkg.image ?? FALLBACK_IMAGE,
    imageAlt: pkg.imageAlt || `${pkg.title} holiday package`,
    gallery: [{ url: pkg.image, alt: pkg.imageAlt }, ...pkg.heroGallery].filter(Boolean),
    description: pkg.longDescription
      ? pkg.longDescription.split('\n\n').filter(Boolean)
      : [pkg.description],
    highlights: pkg.highlights ?? [],
    itinerary: (pkg.itinerary ?? []).map(day => ({
      day: day.day,
      title: day.title,
      description: day.description,
      accommodation: day.accommodation,
      meals: day.meals,
    })),
    inclusions: pkg.inclusions ?? [],
    exclusions: pkg.exclusions ?? [],
    hotels: pkg.hotelExamples?.map(hotel => ({
      name: hotel.name,
      location: hotel.city,
      rating: hotel.rating,
    })) ?? [],
    hotelStars: pkg.hotelExamples?.[0]?.rating,
    bestTime: pkg.bestTimeToVisit,
    visaInfo: pkg.visaInfo,
    flightDuration: pkg.flightDuration,
    groupSize: pkg.groupSize,
    whatToBring: pkg.whatToBring ?? [],
    cancellationPolicy: pkg.cancellationPolicy,
    faqs: pkg.faqs?.length ? pkg.faqs : fallbackFaqs,
    badge: pkg.badge,
  };
}

function sanityToView(pkg: SanityTourDetail): DetailView {
  const description = pkg.description?.length
    ? pkg.description
    : [pkg.tagline || `Book ${pkg.name} from UAE with Big Bird Travel.`];

  return {
    slug: pkg.slug,
    title: pkg.name,
    destination: pkg.destination,
    region: pkg.region,
    themes: pkg.types ?? [],
    days: pkg.duration,
    nights: pkg.nights,
    priceFromAED: pkg.price,
    heroImage: pkg.heroImage || FALLBACK_IMAGE,
    imageAlt: `${pkg.name} holiday package in ${pkg.destination}`,
    gallery: [pkg.heroImage, ...(pkg.gallery ?? [])]
      .filter(Boolean)
      .map((url, index) => ({
        url,
        alt: index === 0 ? `${pkg.name} hero image` : `${pkg.name} gallery image ${index + 1}`,
      })),
    description,
    highlights: pkg.highlights ?? [],
    itinerary: (pkg.itinerary ?? []).map(day => ({
      day: day.day,
      title: day.title,
      description: day.description,
      accommodation: day.accommodation,
      meals: day.meals ? [day.meals] : [],
    })),
    inclusions: pkg.inclusions ?? [],
    exclusions: pkg.exclusions ?? [],
    hotels: pkg.hotelName
      ? [{ name: pkg.hotelName, location: pkg.hotelLocation, rating: pkg.stars }]
      : [],
    hotelStars: pkg.stars,
    bestTime: pkg.bestTime,
    visaInfo: pkg.destination ? `Visa requirements depend on nationality and current ${pkg.destination} rules.` : undefined,
    flightDuration: pkg.departure,
    groupSize: pkg.groupSize,
    whatToBring: pkg.importantNotes ?? [],
    cancellationPolicy: pkg.cancellationPolicy,
    faqs: pkg.faqs?.length ? pkg.faqs : fallbackFaqs,
    badge: pkg.badge,
  };
}

function staticRelatedToView(pkg: Package): RelatedView {
  return {
    slug: pkg.id,
    title: pkg.title,
    destination: pkg.location,
    image: pkg.image,
    imageAlt: pkg.imageAlt,
    nights: pkg.nights,
    days: pkg.days,
    priceFromAED: pkg.priceFromAED,
  };
}

function sanityRelatedToView(pkg: RelatedPackage): RelatedView {
  return {
    slug: pkg.slug.current,
    title: pkg.title,
    destination: pkg.destination,
    image: pkg.hero_image
      ? urlFor(pkg.hero_image).width(700).height(440).fit('crop').auto('format').url()
      : FALLBACK_IMAGE,
    imageAlt: `${pkg.title} holiday package`,
    nights: pkg.duration_nights,
    days: pkg.duration_days,
    priceFromAED: pkg.price_per_person_aed,
  };
}

function Stars({ value = 4 }: { value?: number }) {
  return (
    <span className="flex items-center gap-0.5 text-amber-400">
      {Array.from({ length: 5 }).map((_, index) => (
        <Star key={index} size={14} weight={index < value ? 'fill' : 'regular'} />
      ))}
    </span>
  );
}

function SectionTitle({ children }: { children: string }) {
  return <h2 className="text-2xl font-black tracking-tight text-[#071126] lg:text-3xl">{children}</h2>;
}

function ItineraryAccordion({ days }: { days: DetailItineraryDay[] }) {
  const [open, setOpen] = useState<number | null>(days[0]?.day ?? null);

  if (!days.length) return null;

  return (
    <div className="mt-5 space-y-3">
      {days.map(day => {
        const isOpen = open === day.day;
        return (
          <div
            key={`${day.day}-${day.title}`}
            className={`overflow-hidden rounded-2xl border bg-white shadow-sm transition ${
              isOpen ? 'border-[#0096FF] bg-[#EAF7FF]' : 'border-[#D8EAF7]'
            }`}
          >
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : day.day)}
              className="flex w-full items-center gap-4 px-5 py-4 text-left"
            >
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-[#EAF7FF] text-sm font-black text-[#0068B3]">
                DAY {String(day.day).padStart(2, '0')}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-base font-black text-[#071126]">{day.title}</span>
                {day.accommodation && (
                  <span className="mt-1 block text-xs font-semibold text-[#5F6B7A]">{day.accommodation}</span>
                )}
              </span>
              <CaretDown
                size={18}
                className={`shrink-0 text-[#0068B3] transition ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {isOpen && (
              <div className="border-t border-[#D8EAF7] px-5 pb-5 pt-4">
                {day.description && <p className="text-[15px] leading-7 text-[#5F6B7A]">{day.description}</p>}
                {!!day.meals?.length && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {day.meals.map(meal => (
                      <span key={meal} className="rounded-full bg-white px-3 py-1 text-xs font-bold text-[#0068B3]">
                        {meal}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function BookingCard({ pkg, waQuote }: { pkg: DetailView; waQuote: string }) {
  return (
    <aside className="rounded-3xl border border-[#D8EAF7] bg-white shadow-xl shadow-blue-950/[0.08]">
      <div className="relative aspect-[1.58/1] overflow-hidden rounded-t-3xl">
        <img src={pkg.heroImage} alt={pkg.imageAlt} loading="lazy" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#071126]/55 to-transparent" />
        {pkg.badge && (
          <span className="absolute right-4 top-4 rounded-full bg-[#0077CC] px-3 py-1.5 text-[10px] font-black uppercase tracking-wide text-white">
            {pkg.badge}
          </span>
        )}
      </div>

      <div className="p-5">
        <p className="text-xs font-black uppercase tracking-[0.2em] text-[#0068B3]">Custom quote available</p>
        <h3 className="mt-2 text-xl font-black leading-tight text-[#071126]">{pkg.title}</h3>
        <p className="mt-1 text-sm font-semibold text-[#5F6B7A]">{pkg.destination}</p>

        <div className="mt-4 border-t border-[#D8EAF7] pt-4">
          <p className="text-xs font-bold text-[#5F6B7A]">Starting from</p>
          <p className="mt-1 text-3xl font-black text-[#071126]">{formatPrice(pkg.priceFromAED)}</p>
          {pkg.priceFromAED > 0 && <p className="text-xs font-semibold text-[#5F6B7A]">per person</p>}
        </div>

        <div className="mt-5 grid gap-3 text-sm">
          {[
            [Clock, durationLabel(pkg)],
            [MapPin, pkg.destination],
            [Buildings, pkg.hotelStars ? `${pkg.hotelStars} Star hotel options` : 'Hotel options'],
            [Users, pkg.groupSize ?? 'Custom travelers'],
          ].map(([Icon, text]) => (
            <div key={String(text)} className="flex items-start gap-3 text-[#5F6B7A]">
              <Icon size={18} weight="duotone" className="mt-0.5 shrink-0 text-[#0068B3]" />
              <span className="font-semibold">{String(text)}</span>
            </div>
          ))}
        </div>

        <a
          href={waQuote}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-[#0F7A35] px-5 text-sm font-black uppercase tracking-wide text-white shadow-lg shadow-green-500/20 transition hover:bg-[#1fbd5a]"
        >
          <WhatsappLogo size={18} weight="fill" />
          Get Quote on WhatsApp
        </a>
        <Link
          to="/contact"
          className="mt-3 inline-flex min-h-[48px] w-full items-center justify-center rounded-xl border border-[#0096FF] px-5 text-sm font-black uppercase tracking-wide text-[#0068B3] transition hover:bg-[#EAF7FF]"
        >
          Request Custom Quote
        </Link>

        <div className="mt-5 space-y-2 border-t border-[#D8EAF7] pt-4 text-sm font-semibold text-[#5F6B7A]">
          {['UAE-based support', 'Flexible dates', 'Secure booking', 'Transparent guidance'].map(note => (
            <p key={note} className="flex items-center gap-2">
              <CheckCircle size={16} weight="fill" className="text-[#25D366]" />
              {note}
            </p>
          ))}
        </div>
      </div>
    </aside>
  );
}

// -- Internal-linking clusters -------------------------------------------------
// Classify a package by country from its location so sibling packages (e.g. all
// Indonesia trips) interlink, and so we can point to the right visa page.
function packageCountry(location: string): string {
  const l = location.toLowerCase();
  if (/bali|jakarta|puncak|nusa|indonesia/.test(l)) return 'indonesia';
  if (/kuala lumpur|selangor|langkawi|malaysia/.test(l)) return 'malaysia';
  if (/bangkok|pattaya|phuket|krabi|thailand/.test(l)) return 'thailand';
  if (/maldives/.test(l)) return 'maldives';
  if (/turkey|istanbul|cappadocia/.test(l)) return 'turkey';
  if (/georgia|tbilisi|kazbegi/.test(l)) return 'georgia';
  if (/azerbaijan|baku/.test(l)) return 'azerbaijan';
  if (/armenia/.test(l)) return 'armenia';
  if (/sri lanka|sigiriya|kandy|colombo|bentota|nuwara|galle/.test(l)) return 'srilanka';
  if (/umrah|makkah|madinah|saudi/.test(l)) return 'umrah';
  if (/dubai|abu dhabi|uae/.test(l)) return 'uae';
  return l.split(/[·,]/)[0].trim();
}

// Countries with a dedicated visa page; everything else points to the visa hub.
const VISA_LINK_BY_COUNTRY: Record<string, { href: string; label: string }> = {
  malaysia: { href: '/visas/malaysia-visa', label: 'Malaysia visa' },
  azerbaijan: { href: '/visas/azerbaijan-visa', label: 'Azerbaijan visa' },
};

export default function TourDetail() {
  const { slug = '' } = useParams<{ slug: string }>();
  const loaderData = useLoaderData() as TourPackageDetailLoaderData | undefined;
  const initialTour = loaderData?.tour ?? null;
  const initialRelated = loaderData?.related ?? [];
  const { loading, tour, related: sanityRelated } = useTourPackage(slug, initialTour, initialRelated);

  const staticPackage = PACKAGE_DETAILS.find(pkg => pkg.id === slug);
  const pkg = useMemo<DetailView | null>(() => {
    if (tour) return sanityToView(tour);
    if (staticPackage) return staticToView(staticPackage);
    return null;
  }, [staticPackage, tour]);

  const related = useMemo<RelatedView[]>(() => {
    if (sanityRelated.length) return sanityRelated.map(sanityRelatedToView);
    if (!pkg) return [];
    const country = packageCountry(pkg.destination);
    const pool = PACKAGES.filter(item => item.id !== pkg.slug);
    // Same-country siblings first (e.g. all Indonesia trips link to each other),
    // then fill with a rotated slice of the rest. Rotating by a stable hash of
    // the slug means different package pages surface different "others", so
    // internal links are spread across the whole catalogue instead of always
    // pointing at the first few packages.
    const siblings = pool.filter(item => packageCountry(item.location) === country);
    const others = pool.filter(item => packageCountry(item.location) !== country);
    const seed = others.length
      ? [...pkg.slug].reduce((a, c) => a + c.charCodeAt(0), 0) % others.length
      : 0;
    const rotatedOthers = [...others.slice(seed), ...others.slice(0, seed)];
    return [...siblings, ...rotatedOthers].slice(0, 6).map(staticRelatedToView);
  }, [pkg, sanityRelated]);

  if (loading && !staticPackage && !tour) {
    return (
      <div className="grid min-h-[55vh] place-items-center bg-[#F5FAFF] px-4 text-center">
        <div>
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-[#D8EAF7] border-t-[#0096FF]" />
          <p className="mt-4 text-sm font-bold text-[#5F6B7A]">Loading holiday package...</p>
        </div>
      </div>
    );
  }

  if (!pkg) return <Navigate to="/tour-packages" replace />;

  const waQuote = buildWAUrl(`Hi Big Bird Travel, I want a quote for ${pkg.title}. My travel dates are...`);
  const overviewText = [
    ...pkg.description,
    `This holiday package from UAE is designed for travelers looking for ${pkg.destination} experiences with flexible travel dates, hotel options, and guided support.`,
  ];
  const hasHotelDetails = pkg.hotels.length > 0;
  const hasPracticalInfo = Boolean(pkg.bestTime || pkg.visaInfo || pkg.flightDuration || pkg.groupSize || pkg.whatToBring.length);
  const metaDescription =
    pkg.description[0] ||
    `Book ${pkg.title} from UAE with Big Bird Travel. View itinerary, inclusions, hotel details, price, and WhatsApp quote options.`;

  // Umrah has a dedicated /umrah page. Point this package's canonical (and
  // schema URLs) there to consolidate ranking signals onto one URL and avoid
  // duplicate content between /umrah/ and /tour-packages/umrah/. The sitemap
  // generator reads this canonical, so /tour-packages/umrah/ drops out of the
  // sitemap automatically (deduped against /umrah/).
  const canonicalPath = pkg.slug === 'umrah' ? '/umrah' : `/tour-packages/${pkg.slug}`;

  // Contextual internal links: point each package at the right visa page and the
  // core service pages, so link equity flows to those money pages (which were
  // previously reachable only from the nav/footer).
  const pkgCountry = packageCountry(pkg.destination);
  const visaLink = VISA_LINK_BY_COUNTRY[pkgCountry] ?? { href: '/visas', label: 'UAE & international visa assistance' };

  return (
    <div className="overflow-hidden bg-[#F5FAFF] text-[#071126]">
      <SEO
        title={`${pkg.title} | Holiday Package from UAE`}
        description={metaDescription}
        canonical={canonicalPath}
        schema={[
          AGENCY_SCHEMA,
          serviceSchema(pkg.title, metaDescription, canonicalPath),
          breadcrumbSchema([
            { name: 'Home', item: '/' },
            { name: 'Holiday Packages', item: '/tour-packages' },
            { name: pkg.title, item: canonicalPath },
          ]),
          faqSchema(pkg.faqs),
          packageSchema({
            name: pkg.title,
            description: metaDescription,
            url: canonicalPath,
            image: pkg.heroImage,
            lowPrice: Math.max(pkg.priceFromAED, 0),
          }),
        ]}
        skipSuffix
      />

      <section className="relative overflow-hidden bg-[#071126] text-white">
        <img
          src={pkg.heroImage}
          alt={pkg.imageAlt}
          loading="eager"
          className="absolute inset-0 h-full w-full object-cover opacity-65"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#071126] via-[#071126]/78 to-[#071126]/30" />
        <img
          src="/assets/backgrounds/flight_path_with_trailing_jet.webp"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute right-0 top-6 hidden w-[42rem] opacity-[0.08] lg:block" loading="lazy" />
        <div className="relative mx-auto max-w-[1500px] px-4 py-12 sm:px-6 lg:py-16">
          <nav className="mb-8 flex flex-wrap items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-white/60">
            <Link to="/" className="hover:text-white">Home</Link>
            <span>/</span>
            <Link to="/tour-packages" className="hover:text-white">Holiday Packages</Link>
            <span>/</span>
            <span className="text-[#8DCAFF]">{pkg.title}</span>
          </nav>

          <motion.div {...fadeUp} className="max-w-4xl">
            <div className="mb-4 flex flex-wrap gap-2">
              {pkg.badge && (
                <span className="rounded-full bg-[#0077CC] px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-white">
                  {pkg.badge}
                </span>
              )}
              {pkg.region && (
                <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-white">
                  {pkg.region}
                </span>
              )}
            </div>

            <h1 className="text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-[64px]">
              {pkg.title}
            </h1>
            <p className="mt-5 max-w-3xl text-[17px] leading-8 text-white/82">
              {metaDescription}
            </p>

            <div className="mt-7 flex flex-wrap gap-4 text-sm font-bold text-white/82">
              <span className="flex items-center gap-2"><Clock size={18} className="text-[#0068B3]" />{durationLabel(pkg)}</span>
              <span className="flex items-center gap-2"><MapPin size={18} className="text-[#0068B3]" />{pkg.destination}</span>
              {!!pkg.themes.length && (
                <span className="flex items-center gap-2"><Heart size={18} className="text-[#0068B3]" />{pkg.themes.slice(0, 3).join(', ')}</span>
              )}
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={waQuote}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-[54px] items-center justify-center gap-2 rounded-xl bg-[#0F7A35] px-7 text-sm font-black uppercase tracking-wide text-white shadow-xl shadow-green-500/20 transition hover:bg-[#1fbd5a]"
              >
                <WhatsappLogo size={20} weight="fill" />
                Get Quote on WhatsApp
              </a>
              {pkg.itinerary.length > 0 && (
                <a
                  href="#itinerary"
                  className="inline-flex min-h-[54px] items-center justify-center gap-2 rounded-xl border border-white/30 bg-white/10 px-7 text-sm font-black uppercase tracking-wide text-white transition hover:bg-white/15"
                >
                  View Itinerary
                  <ArrowRight size={17} />
                </a>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="relative z-10 -mt-8 px-4 sm:px-6">
        <div className="mx-auto grid max-w-[1500px] gap-3 rounded-2xl border border-[#D8EAF7] bg-white p-4 shadow-xl shadow-blue-950/[0.08] sm:grid-cols-2 lg:grid-cols-6">
          {[
            [Clock, 'Duration', durationLabel(pkg)],
            [CalendarBlank, 'Starting Price', formatPrice(pkg.priceFromAED)],
            [MapPin, 'Destination', pkg.destination],
            [Buildings, 'Hotel Category', pkg.hotelStars ? `${pkg.hotelStars} Star Options` : 'Hotel options'],
            [Heart, 'Best For', pkg.themes.slice(0, 2).join(', ') || 'Custom travel'],
            [ShieldCheck, 'Package Type', 'Customizable'],
          ].map(([Icon, label, value]) => (
            <div key={String(label)} className="flex items-center gap-3 rounded-xl bg-[#F5FAFF] px-4 py-3">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#EAF7FF] text-[#0068B3]">
                <Icon size={20} weight="duotone" />
              </span>
              <span>
                <span className="block text-[10px] font-black uppercase tracking-wider text-[#5F6B7A]">{String(label)}</span>
                <span className="mt-0.5 block text-sm font-black text-[#071126]">{String(value)}</span>
              </span>
            </div>
          ))}
        </div>
      </section>

      <main className="relative px-4 py-12 sm:px-6 lg:py-14">
        <img
          src="/assets/backgrounds/minimalist_city_skyline_in_pale_blue.webp"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-64 w-full object-cover object-bottom opacity-[0.10]" loading="lazy" />
        <div className="relative mx-auto grid max-w-[1500px] gap-8 lg:grid-cols-[1fr_360px]">
          <div className="space-y-8">
            <motion.section {...fadeUp} className="rounded-3xl border border-[#D8EAF7] bg-white p-6 shadow-sm lg:p-8">
              <SectionTitle>Package Overview</SectionTitle>
              <div className="mt-5 space-y-4">
                {overviewText.map((paragraph, index) => (
                  <p key={index} className="text-[16px] leading-8 text-[#5F6B7A]">{paragraph}</p>
                ))}
              </div>
            </motion.section>

            <div className="lg:hidden">
              <BookingCard pkg={pkg} waQuote={waQuote} />
            </div>

            {!!pkg.highlights.length && (
              <motion.section {...fadeUp} className="rounded-3xl border border-[#D8EAF7] bg-white p-6 shadow-sm lg:p-8">
                <SectionTitle>Package Highlights</SectionTitle>
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  {pkg.highlights.map(item => (
                    <div key={item} className="flex items-start gap-3 rounded-2xl border border-[#D8EAF7] bg-[#F5FAFF] p-4">
                      <CheckCircle size={20} weight="fill" className="mt-0.5 shrink-0 text-[#25D366]" />
                      <p className="text-[15px] font-bold leading-6 text-[#071126]">{item}</p>
                    </div>
                  ))}
                </div>
              </motion.section>
            )}

            {!!pkg.itinerary.length && (
              <motion.section id="itinerary" {...fadeUp} className="rounded-3xl border border-[#D8EAF7] bg-white p-6 shadow-sm lg:p-8">
                <SectionTitle>Day-by-Day Itinerary</SectionTitle>
                <p className="mt-3 text-[15px] leading-7 text-[#5F6B7A]">
                  Daily order may vary based on flight timings, hotel availability, and local conditions.
                </p>
                <ItineraryAccordion days={pkg.itinerary} />
              </motion.section>
            )}

            {!!pkg.inclusions.length && (
              <motion.section {...fadeUp} className="rounded-3xl border border-[#D8EAF7] bg-white p-6 shadow-sm lg:p-8">
                <SectionTitle>What's Included</SectionTitle>
                <div className={`mt-5 grid gap-5 ${pkg.exclusions.length ? 'lg:grid-cols-2' : ''}`}>
                  <div className="rounded-2xl bg-[#F5FAFF] p-5">
                    <p className="mb-4 text-xs font-black uppercase tracking-[0.2em] text-[#25D366]">Included</p>
                    <ul className="space-y-3">
                      {pkg.inclusions.map(item => (
                        <li key={item} className="flex items-start gap-3 text-[15px] font-semibold leading-6 text-[#071126]">
                          <CheckCircle size={18} weight="fill" className="mt-0.5 shrink-0 text-[#25D366]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {!!pkg.exclusions.length && (
                    <div className="rounded-2xl bg-[#F5FAFF] p-5">
                      <p className="mb-4 text-xs font-black uppercase tracking-[0.2em] text-red-500">Not Included</p>
                      <ul className="space-y-3">
                        {pkg.exclusions.map(item => (
                          <li key={item} className="flex items-start gap-3 text-[15px] font-semibold leading-6 text-[#5F6B7A]">
                            <XCircle size={18} weight="fill" className="mt-0.5 shrink-0 text-red-400" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </motion.section>
            )}

            {hasHotelDetails && (
              <motion.section {...fadeUp} className="rounded-3xl border border-[#D8EAF7] bg-white p-6 shadow-sm lg:p-8">
                <SectionTitle>Hotel Details</SectionTitle>
                <div className="mt-5 grid gap-4 md:grid-cols-2">
                  {pkg.hotels.map(hotel => (
                    <div key={hotel.name} className="rounded-2xl border border-[#D8EAF7] bg-[#F5FAFF] p-5">
                      <Stars value={hotel.rating ?? pkg.hotelStars ?? 4} />
                      <h3 className="mt-3 text-lg font-black text-[#071126]">{hotel.name}</h3>
                      {hotel.location && <p className="mt-1 text-sm font-semibold text-[#5F6B7A]">{hotel.location}</p>}
                    </div>
                  ))}
                </div>
                <p className="mt-4 rounded-2xl bg-[#EAF7FF] px-4 py-3 text-sm font-semibold leading-6 text-[#5F6B7A]">
                  Hotel availability may vary by travel date. Similar category hotels may be offered if selected hotel is unavailable.
                </p>
              </motion.section>
            )}

            {hasPracticalInfo && (
              <motion.section {...fadeUp} className="rounded-3xl border border-[#D8EAF7] bg-white p-6 shadow-sm lg:p-8">
                <SectionTitle>Practical Travel Information</SectionTitle>
                <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {[
                    [CalendarBlank, 'Best Time to Visit', pkg.bestTime],
                    [Info, 'Visa Information', pkg.visaInfo],
                    [AirplaneTilt, 'Departure / Route', pkg.flightDuration],
                    [Users, 'Group Size', pkg.groupSize],
                    [SuitcaseRolling, 'What to Bring', pkg.whatToBring.slice(0, 3).join(', ')],
                    [Heart, 'Family Suitability', 'Suitable for families, couples, and custom groups'],
                  ]
                    .filter(([, , value]) => Boolean(value))
                    .map(([Icon, label, value]) => (
                      <div key={String(label)} className="rounded-2xl border border-[#D8EAF7] bg-[#F5FAFF] p-4">
                        <Icon size={22} weight="duotone" className="text-[#0068B3]" />
                        <p className="mt-3 text-xs font-black uppercase tracking-[0.16em] text-[#5F6B7A]">{String(label)}</p>
                        <p className="mt-1 text-[14px] font-bold leading-6 text-[#071126]">{String(value)}</p>
                      </div>
                    ))}
                </div>
              </motion.section>
            )}

            <motion.section {...fadeUp} className="rounded-3xl border border-amber-200 bg-amber-50 p-5 shadow-sm">
              <div className="flex items-start gap-3">
                <Warning size={24} weight="duotone" className="mt-0.5 shrink-0 text-amber-600" />
                <div>
                  <h2 className="text-lg font-black text-[#071126]">Policy & Price Note</h2>
                  <p className="mt-2 text-[15px] leading-7 text-[#5F6B7A]">
                    {pkg.cancellationPolicy || 'Prices are subject to availability. Final price depends on travel dates, airline fares, hotel availability, room category, and selected inclusions.'}
                  </p>
                </div>
              </div>
            </motion.section>

            {!!pkg.faqs.length && (
              <motion.section {...fadeUp} className="rounded-3xl border border-[#D8EAF7] bg-white p-6 shadow-sm lg:p-8">
                <SectionTitle>Frequently Asked Questions</SectionTitle>
                <div className="mt-5 space-y-3">
                  {pkg.faqs.map((faq, index) => (
                    <details
                      key={faq.q}
                      open={index === 0}
                      className="group rounded-2xl border border-[#D8EAF7] bg-white px-5 py-4 shadow-sm transition [&[open]]:border-[#0096FF] [&[open]]:bg-[#EAF7FF]"
                    >
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[16px] font-black text-[#071126]">
                        {faq.q}
                        <CaretDown className="shrink-0 text-[#0068B3] transition group-open:rotate-180" size={16} />
                      </summary>
                      <p className="mt-3 text-[15px] leading-7 text-[#5F6B7A]">{faq.a}</p>
                    </details>
                  ))}
                </div>
              </motion.section>
            )}
          </div>

          <div className="hidden lg:block">
            <div className="sticky top-6">
              <BookingCard pkg={pkg} waQuote={waQuote} />
            </div>
          </div>
        </div>
      </main>

      {/* Contextual internal links — feeds link equity to the core service pages */}
      <section className="px-4 py-10 sm:px-6 bg-[#F5FAFF]">
        <div className="mx-auto max-w-[1100px]">
          <h2 className="mb-3 text-2xl font-black tracking-tight text-[#071126]">
            Plan your {pkg.destination} trip from the UAE
          </h2>
          <p className="text-[15px] leading-relaxed text-[#3f4a5a]">
            Big Bird Travel &amp; Tourism handles every part of your {pkg.destination} holiday from the UAE.
            Sort your <Link to={visaLink.href} className="font-semibold text-[#0068B3] hover:underline">{visaLink.label}</Link>,
            book your <Link to="/flights" className="font-semibold text-[#0068B3] hover:underline">air tickets</Link> and{' '}
            <Link to="/hotels" className="font-semibold text-[#0068B3] hover:underline">hotel reservations</Link>, add{' '}
            <Link to="/insurance" className="font-semibold text-[#0068B3] hover:underline">travel insurance</Link>, or browse all our{' '}
            <Link to="/tour-packages" className="font-semibold text-[#0068B3] hover:underline">holiday packages from UAE</Link>.
            Staying closer to home? Explore our{' '}
            <Link to="/uae-tours" className="font-semibold text-[#0068B3] hover:underline">UAE tours</Link> or{' '}
            <Link to="/umrah" className="font-semibold text-[#0068B3] hover:underline">Umrah packages</Link>, and{' '}
            <Link to="/contact" className="font-semibold text-[#0068B3] hover:underline">contact our Abu Dhabi team</Link> for a tailored quote.
          </p>
        </div>
      </section>

      {!!related.length && (
        <section className="px-4 py-12 sm:px-6 lg:py-14">
          <div className="mx-auto max-w-[1500px]">
            <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <p className="mb-3 text-xs font-black uppercase tracking-[0.32em] text-[#0068B3]">Explore More</p>
                <h2 className="text-3xl font-black tracking-tight text-[#071126]">Related Holiday Packages</h2>
              </div>
              <Link to="/tour-packages" className="inline-flex items-center gap-2 text-sm font-black uppercase tracking-wide text-[#0068B3]">
                View all packages
                <ArrowRight size={16} />
              </Link>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {related.map(item => (
                <article key={item.slug} className="overflow-hidden rounded-2xl border border-[#D8EAF7] bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
                  <Link to={`/tour-packages/${item.slug}`} className="block aspect-[1.72/1] overflow-hidden">
                    <img src={item.image} alt={item.imageAlt} loading="lazy" className="h-full w-full object-cover transition duration-500 hover:scale-105" />
                  </Link>
                  <div className="p-5">
                    <p className="text-xs font-black uppercase tracking-wide text-[#0068B3]">{item.destination}</p>
                    <h3 className="mt-2 text-lg font-black leading-tight text-[#071126]">{item.title}</h3>
                    <p className="mt-2 text-sm font-semibold text-[#5F6B7A]">{durationLabel(item)}</p>
                    <div className="mt-4 flex items-center justify-between gap-3">
                      <p className="font-black text-[#0068B3]">{formatPrice(item.priceFromAED)}</p>
                      <Link to={`/tour-packages/${item.slug}`} className="inline-flex items-center gap-1 text-sm font-black text-[#0068B3]">
                        View Details
                        <ArrowRight size={14} />
                      </Link>
                    </div>
                    <a
                      href={buildWAUrl(`Hi Big Bird Travel, I want a quote for ${item.title}.`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-[#0F7A35] px-4 text-sm font-black text-white"
                    >
                      <WhatsappLogo size={17} weight="fill" />
                      Quote on WhatsApp
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="px-4 pb-28 sm:px-6 sm:pb-16">
        <div className="relative mx-auto flex max-w-[1500px] flex-col gap-5 overflow-hidden rounded-2xl bg-[#071126] p-6 text-white shadow-xl shadow-blue-950/20 md:flex-row md:items-center md:justify-between">
          <img
            src="/assets/backgrounds/decorative_flight_path_illustration.webp"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute right-0 top-0 hidden w-80 opacity-[0.10] lg:block" loading="lazy" />
          <div className="relative flex items-center gap-4">
            <span className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-[#0077CC] text-white">
              <PaperPlaneTilt size={30} weight="duotone" />
            </span>
            <div>
              <h2 className="text-2xl font-black">Ready to book this holiday package?</h2>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-white/70">
                Share your travel dates, number of travelers, and preferred hotel category. Our UAE travel team will confirm availability and prepare your quote.
              </p>
              <p className="mt-3 text-xs font-black uppercase tracking-wide text-[#8DCAFF]">
                UAE-based support - Custom packages - Flexible dates - Transparent guidance
              </p>
            </div>
          </div>
          <div className="relative flex flex-col gap-3 sm:flex-row">
            <a
              href={waQuote}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#0F7A35] px-7 py-4 text-sm font-black uppercase tracking-wide text-white"
            >
              <WhatsappLogo size={18} weight="fill" />
              Get Quote on WhatsApp
            </a>
            <Link
              to="/tour-packages"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 px-7 py-4 text-sm font-black uppercase tracking-wide text-white hover:bg-white/10"
            >
              View All Packages
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
